import { Button, Icon, Menu, MenuItem, NumericInput, Slider } from "@blueprintjs/core";
import { Classes, Popover2 } from "@blueprintjs/popover2";
import React, { useEffect, useState } from "react";

export function Position(editor) {
	const { Canvaseditor, disabledObj } = editor;

	const [rotation, setRotation] = useState(0);
	const [posX, setPosX] = useState(0);
	const [posY, setPosY] = useState(0);
	const [sizeW, setSizeW] = useState(0);
	const [sizeH, setSizeH] = useState(0);
	const [hasSelection, setHasSelection] = useState(false);

	const notifyModified = () => {
		if (typeof Canvaseditor.fire === "function") Canvaseditor.fire("object:modified");
	};

	const syncFromActiveObject = () => {
		if (!Canvaseditor) return;
		const obj = Canvaseditor.getActiveObject();
		if (!obj) {
			setHasSelection(false);
			return;
		}

		setHasSelection(true);

		const bounds = obj.getBoundingRect(true, true);
		const angle = ((obj.angle || 0) % 360 + 360) % 360;

		setRotation(Math.round(angle));
		setPosX(Math.round(bounds.left));
		setPosY(Math.round(bounds.top));
		setSizeW(Math.round(bounds.width));
		setSizeH(Math.round(bounds.height));
	};

	useEffect(() => {
		if (!Canvaseditor) return;

		const handleSelectionChanged = () => {
			syncFromActiveObject();
		};

		const handleSelectionCleared = () => {
			setHasSelection(false);
		};

		syncFromActiveObject();

		Canvaseditor.on("selection:created", handleSelectionChanged);
		Canvaseditor.on("selection:updated", handleSelectionChanged);
		Canvaseditor.on("selection:cleared", handleSelectionCleared);
		Canvaseditor.on("object:modified", handleSelectionChanged);

		return () => {
			Canvaseditor.off("selection:created", handleSelectionChanged);
			Canvaseditor.off("selection:updated", handleSelectionChanged);
			Canvaseditor.off("selection:cleared", handleSelectionCleared);
			Canvaseditor.off("object:modified", handleSelectionChanged);
		};
	}, [Canvaseditor]);

	// Hanlde position element.
	const handleLeftAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			Canvaseditor.getActiveObject().set(
				"left",
				activeObject.left + -boundBox.left
			);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleRightAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			let rightValue =
				activeObject.left + Canvaseditor.width - boundBox.left - boundBox.width;
			Canvaseditor.getActiveObject().set("left", rightValue);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleCenterAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			let centerValue =
				activeObject.left +
				(Canvaseditor.width - boundBox.width) / 2 -
				boundBox.left;
			Canvaseditor.getActiveObject().set("left", centerValue);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleTopAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			let topValue = activeObject.top + -boundBox.top;
			Canvaseditor.getActiveObject().set("top", topValue);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleMiddleAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			let middleValue =
				activeObject.top +
				(Canvaseditor.height - boundBox.height) / 2 -
				boundBox.top;
			Canvaseditor.getActiveObject().set("top", middleValue);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleBottomAlignment = () => {
		if (Canvaseditor.getActiveObject()) {
			let boundBox = Canvaseditor.getActiveObject().getBoundingRect(true, true);
			let activeObject = Canvaseditor.getActiveObject();
			let bottomValue =
				activeObject.top + Canvaseditor.height - boundBox.top - boundBox.height;
			Canvaseditor.getActiveObject().set("top", bottomValue);
			Canvaseditor.renderAll();
			notifyModified();
		}
	};

	const handleRotateChange = (value) => {
		setRotation(value);
		if (!Canvaseditor) return;
		const obj = Canvaseditor.getActiveObject();
		if (!obj) return;
		obj.rotate(value);
		obj.setCoords();
		Canvaseditor.renderAll();
		notifyModified();
	};

	const handlePositionChange = (key, value) => {
		if (!Canvaseditor || isNaN(value)) return;
		const obj = Canvaseditor.getActiveObject();
		if (!obj) return;

		const bounds = obj.getBoundingRect(true, true);

		if (key === "x") {
			setPosX(value);
			const dx = value - bounds.left;
			obj.set("left", obj.left + dx);
		} else if (key === "y") {
			setPosY(value);
			const dy = value - bounds.top;
			obj.set("top", obj.top + dy);
		}

		obj.setCoords();
		Canvaseditor.renderAll();
		notifyModified();
	};

	const handleSizeChange = (key, value) => {
		if (!Canvaseditor || isNaN(value) || value <= 0) return;
		const obj = Canvaseditor.getActiveObject();
		if (!obj) return;

		const bounds = obj.getBoundingRect(true, true);

		if (key === "w" && bounds.width > 0) {
			setSizeW(value);
			const scaleX = (obj.scaleX || 1) * (value / bounds.width);
			obj.set("scaleX", scaleX);
		}

		if (key === "h" && bounds.height > 0) {
			setSizeH(value);
			const scaleY = (obj.scaleY || 1) * (value / bounds.height);
			obj.set("scaleY", scaleY);
		}

		obj.setCoords();
		Canvaseditor.renderAll();
		notifyModified();
	};

	return (
		<div className='position'>
			<Popover2
				interactionKind='click'
				popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
				placement='bottom'
				content={
					<div
						style={{
							display: "flex",
							padding: 8,
							boxSizing: "border-box",
							gap: 12,
							maxWidth: 420,
						}}
					>
						<Menu style={{ minWidth: 150, flexShrink: 0, margin: 0 }}>
							<MenuItem
								onClick={handleLeftAlignment}
								icon='alignment-left'
								text='Align left'
							/>
							<MenuItem
								onClick={handleCenterAlignment}
								icon='alignment-vertical-center'
								text='Align center'
							/>
							<MenuItem
								onClick={handleRightAlignment}
								icon='alignment-right'
								text='Align right'
							/>
							<MenuItem
								onClick={handleTopAlignment}
								icon='alignment-top'
								text='Align top'
							/>
							<MenuItem
								onClick={handleMiddleAlignment}
								icon='alignment-horizontal-center'
								text='Align middle'
							/>
							<MenuItem
								onClick={handleBottomAlignment}
								icon='alignment-bottom'
								text='Align bottom'
							/>
						</Menu>

						<div style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 10 }}>
							<div>
								<div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Rotate</div>
								<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
									<Slider
										min={0}
										max={360}
										stepSize={1}
										labelStepSize={90}
										value={rotation}
										onChange={handleRotateChange}
										disabled={!hasSelection || disabledObj}
										style={{ width: "100%" }}
									/>
									<NumericInput
										min={0}
										max={360}
										value={rotation}
										onValueChange={(value) => handleRotateChange(Number(value) || 0)}
										disabled={!hasSelection || disabledObj}
										style={{ width: "50%" }}
									/>
								</div>
							</div>

							<div>
								<div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Info</div>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
										gap: 8,
									}}
								>
									<div>
										<div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>X</div>
										<NumericInput
											value={posX}
											onValueChange={(value) => handlePositionChange("x", Number(value))}
											disabled={!hasSelection || disabledObj}
											style={{ width: "100%" }}
										/>
									</div>
									<div>
										<div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>Y</div>
										<NumericInput
											value={posY}
											onValueChange={(value) => handlePositionChange("y", Number(value))}
											disabled={!hasSelection || disabledObj}
											style={{ width: "100%" }}
										/>
									</div>
									<div>
										<div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>W</div>
										<NumericInput
											min={1}
											value={sizeW}
											onValueChange={(value) => handleSizeChange("w", Number(value))}
											disabled={!hasSelection || disabledObj}
											style={{ width: "100%" }}
										/>
									</div>
									<div>
										<div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>H</div>
										<NumericInput
											min={1}
											value={sizeH}
											onValueChange={(value) => handleSizeChange("h", Number(value))}
											disabled={!hasSelection || disabledObj}
											style={{ width: "100%" }}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				}
				renderTarget={({ isOpen, ref, ...targetProps }) => (
					<Button
						{...targetProps}
						elementRef={ref}
						disabled={disabledObj}
						icon={<Icon icon='layers' />}
						text='Position'
					/>
				)}
			/>
		</div>
	);
}
